import React, { TouchEvent, useRef, useState } from "react";

import { useRouter } from "next/router";

import { ProductDetailsArButton } from "./ProductDetailsArButton";
import ProductDetailsLabel from "./ProductDetailsLabel";
import ProductImagesLightBox from "./ProductImagesLightBox";
import { Search as SearchIcon } from "@/assets/icons/24/Search";
import { BunnyPicture } from "@/common/components/bunny-picture";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { buildProductImageAlt } from "@/common/utils/product-image.utils";
import { ProductDetailsFavoriteButton } from "@whoppah/components/ProductDetail/ProductDetailsFavoriteButton";
import { ProductDetailsShareButton } from "@whoppah/components/ProductDetail/ProductDetailsShareButton";
import { ChevronLeft as ChevronLeftIcon } from "@whoppah/icons/24/ChevronLeft";
import { ChevronRight as ChevronRightIcon } from "@whoppah/icons/24/ChevronRight";
import EyeIcon from "@whoppah/icons/24/Eye";
import HeartIcon from "@whoppah/icons/24/heart/Filled";
import { Body } from "@whoppah/ui/typography/Body";

type ProductDetailsCarouselChevronProps = {
  imageCount: number;
  imageIndex?: number;
  direction?: "left" | "right";
  onClick?: (pageIndex: number) => void;
};

const ProductDetailsCarouselChevron = ({
  imageCount,
  imageIndex = 0,
  direction = "left",
  onClick = undefined,
}: ProductDetailsCarouselChevronProps) => {
  const handleClick = () => {
    let newIndex = imageIndex;

    if (direction === "left") {
      newIndex = imageIndex - 1;
      if (newIndex < 0) {
        newIndex = imageCount - 1;
      }
    }

    if (direction === "right") {
      newIndex = imageIndex + 1;
      if (newIndex >= imageCount) {
        newIndex = 0;
      }
    }

    onClick?.(newIndex);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "absolute z-10 rounded-full border-[1px] border-gray-300 bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] md:static md:border-0 md:shadow-inherit",
        direction === "left" ? "left-4" : "right-4"
      )}
    >
      {direction === "left" ? (
        <ChevronLeftIcon className="min-w-[1.5rem] md:h-10 md:w-10 md:min-w-[2.5rem]" />
      ) : (
        <ChevronRightIcon className="min-w-[1.5rem] md:h-10 md:w-10 md:min-w-[2.5rem]" />
      )}
    </button>
  );
};

type ProductDetailsCarouselInfoAndButtonsProps = {
  product: ProductBySlugNonNullable;
};

const ProductDetailsCarouselInfoAndButtons = ({
  product,
}: ProductDetailsCarouselInfoAndButtonsProps) => {
  return (
    <>
      <span className="absolute bottom-4 left-4 z-[10] flex flex-col space-y-4 md:mt-0 md:hidden">
        <span className="flex items-center space-x-4">
          <EyeIcon className="text-white" />
          <Body as="span" className="text-white">
            {product.view_count}
          </Body>
        </span>
        <span className="flex items-center space-x-4">
          <HeartIcon className="text-white" />
          <Body as="span" className="text-white">
            {product.favorite_count}
          </Body>
        </span>
      </span>
      <span className="absolute bottom-4 right-4 z-[10] flex flex-col space-y-4 md:mt-0 md:hidden">
        <ProductDetailsArButton product={product} />
        <ProductDetailsShareButton transparent={false} />
        <ProductDetailsFavoriteButton product={product} transparent={false} />
      </span>
    </>
  );
};

const SWIPE_THRESHOLD = 50;

type ProductDetailsCarouselProps = {
  product: ProductBySlugNonNullable;
  className?: string;
};

export const ProductDetailsCarousel = ({
  product,
  className,
}: ProductDetailsCarouselProps) => {
  const { locale } = useRouter();
  const [index, setIndex] = useState(0);
  const [isPreview, setIsPreview] = useState(false);

  const touchStartXRef = useRef(0);

  const images = product.images;

  const handleChangePage = (pageIndex: number) => {
    setIndex(pageIndex);

    /** Trigger `image_browse_pdp` event. */
    analyticsService.trackImageBrowsePdp();
  };

  const handleTouchStart = (evt: TouchEvent) => {
    const firstTouch = evt.touches.item(0);
    if (firstTouch) {
      touchStartXRef.current = firstTouch.clientX;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const firstTouch = e.changedTouches.item(0);

    if (firstTouch) {
      const diff = touchStartXRef.current - firstTouch.clientX;

      // Swipe right
      const isSwipeRight = diff < -SWIPE_THRESHOLD;

      if (isSwipeRight) {
        setIndex(prev => {
          let newIndex = prev - 1;
          if (newIndex < 0) {
            newIndex = images.length - 1;
          }
          return newIndex;
        });
      }

      // Swipe left
      const isSwipeLeft = diff > SWIPE_THRESHOLD;

      if (isSwipeLeft) {
        setIndex(prev => {
          let newIndex = prev + 1;
          if (newIndex >= images.length) {
            newIndex = 0;
          }
          return newIndex;
        });
      }

      if (isSwipeRight || isSwipeLeft) {
        /** Trigger `image_browse_pdp` event. */
        analyticsService.trackImageBrowsePdp();
      }
    }
  };

  const handleImageClick = () => {
    setIsPreview(true);
  };

  const handleCloseLightBox = () => {
    setIsPreview(false);
  };

  return (
    <div
      className={cn(
        "min-h-auto relative -ml-3 -mr-3 -mt-2 flex h-[100vw] flex-col items-center md:ml-0 md:mr-0 md:mt-0 md:h-auto md:min-h-[32rem] md:space-y-4",
        className
      )}
    >
      <div className="relative flex flex-1 items-center self-stretch">
        <ProductDetailsCarouselChevron
          imageCount={images.length}
          imageIndex={index}
          direction="left"
          onClick={handleChangePage}
        />
        <div
          className="relative h-full flex-1"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {images.map((image, imageIndex) => (
            <React.Fragment key={image.id}>
              <BunnyPicture
                preload={imageIndex === 0}
                draggable={false}
                className="absolute bottom-0 left-0 right-0 top-0 overflow-hidden"
                imgClassName={cn(
                  "h-full w-full object-cover object-center md:object-contain",
                  imageIndex === index ? "left-0" : "left-[100vw] hidden"
                )}
                src={image.url}
                width={image.width ?? 0}
                height={image.height ?? 0}
                sizes={[{ media: "(max-width: 768px)", width: "560px" }]}
                alt={buildProductImageAlt(
                  product,
                  imageIndex,
                  images.length,
                  locale
                )}
              />
              <div
                onClick={() => handleImageClick()}
                className="absolute top-0 flex h-full w-full cursor-pointer items-center justify-center bg-[#282F36] text-white opacity-0 transition duration-300 ease-in-out"
              >
                {!isPreview && (
                  <SearchIcon className="h-[3.75rem] w-[3.75rem]" />
                )}
              </div>
            </React.Fragment>
          ))}
          <ProductImagesLightBox
            productId={product.id}
            imageIndex={index}
            images={images}
            isOpen={isPreview}
            onClose={handleCloseLightBox}
          />
        </div>
        <ProductDetailsCarouselChevron
          imageCount={images.length}
          imageIndex={index}
          direction="right"
          onClick={handleChangePage}
        />
        <ProductDetailsCarouselInfoAndButtons product={product} />
      </div>
      {index === 0 && (
        <ProductDetailsLabel
          product={product}
          className="absolute left-2 top-2"
        />
      )}
    </div>
  );
};
